/**
 * Diff de progression des quêtes autour d'une leçon Reps — logique PURE
 * (aucun appel réseau). Portage 1:1 de reps-app/src/features/quests/
 * quest-progress.ts : les deux plateformes doivent raconter la même histoire.
 *
 * Le serveur est 100 % autoritaire et ne renvoie aucun delta : on compare un
 * instantané pris au montage de la leçon avec un refetch fait APRÈS le submit
 * (Symfony fait avancer les quêtes avant de répondre — un refetch lancé après
 * sa résolution est donc garanti à jour).
 */

/**
 * Diff avant/après par quest.id (jamais par index). Vide si le jour local a
 * changé entre les deux instantanés (passage de minuit : jeu de quêtes
 * renouvelé) ou si rien n'a bougé. Une quête déjà complétée avant la leçon
 * n'est jamais remontrée, même si sa progression brute continue de croître.
 */
export function diffQuests(before, after) {
    if (!before?.quests?.length || !after?.quests?.length) return [];
    if (before.date && after.date && before.date !== after.date) return [];
    const prev = new Map(before.quests.map((q) => [String(q.id), q]));
    const deltas = [];
    for (const quest of after.quests) {
        const b = prev.get(String(quest.id));
        if (!b || b.completed === true) continue;
        if (quest.targetValue <= 0) continue;
        const from = Math.min(b.currentProgress, quest.targetValue);
        const to = Math.min(quest.currentProgress, quest.targetValue);
        if (to <= from) continue;
        deltas.push({
            quest,
            from,
            to,
            completedNow: quest.completed === true,
        });
    }
    return deltas;
}

/**
 * Assemble l'écran ; null = rien à célébrer (aucune barre ne bougerait).
 * badgeBefore (instantané pris au montage) donne le compteur de départ exact
 * et le « déjà gagné » ; à défaut on retranche completedCount du compteur
 * serveur (approximation suffisante).
 *
 * Retourne { cards, deltas, completedCount, badge } :
 * - cards : TOUTES les quêtes du jour (celles qui n'ont pas bougé sont des
 *   barres statiques), dans l'ordre du serveur ;
 * - badge : présent seulement si une quête vient d'être terminée (c'est lui
 *   qui reçoit le « +N ») et si son état a pu être rafraîchi.
 */
export function buildQuestsUpdate(before, after, badgeCurrent, badgeBefore) {
    const deltas = diffQuests(before, after);
    if (deltas.length === 0) return null;
    const byId = new Map(deltas.map((d) => [String(d.quest.id), d]));
    const cards = (after?.quests ?? [])
        .filter((quest) => quest.targetValue > 0)
        .map((quest) => {
            const delta = byId.get(String(quest.id));
            if (delta) return { ...delta, changed: true };
            const value = Math.min(quest.currentProgress, quest.targetValue);
            return {
                quest,
                from: value,
                to: value,
                changed: false,
                completedNow: false,
            };
        });
    const completedCount = deltas.filter((d) => d.completedNow).length;
    // L'instantané d'avant ne vaut que pour le MÊME badge (passage de mois).
    const sameBadge =
        badgeBefore != null &&
        badgeCurrent != null &&
        badgeBefore.badgeId === badgeCurrent.badgeId;
    const badge =
        completedCount > 0 && badgeCurrent
            ? {
                  current: badgeCurrent,
                  fromCompleted: sameBadge
                      ? Math.min(
                            badgeBefore.questCompleted,
                            badgeCurrent.questCompleted
                        )
                      : Math.max(0, badgeCurrent.questCompleted - completedCount),
                  awardedBefore: sameBadge && badgeBefore.isAwarded === true,
                  // On avait un instantané fiable du MÊME badge avant la leçon.
                  // Sans lui, awardedBefore vaut false par défaut et on
                  // fêterait un badge décroché un autre jour : l'écran de
                  // déblocage doit exiger ce témoin.
                  hadBefore: sameBadge,
              }
            : null;
    return { cards, deltas, completedCount, badge };
}
